import type { IconDefinition } from '@fortawesome/fontawesome-common-types';

export type APIList<T> = {
  object?: 'list';
  total_count: number;
  data: T[];
  next?: string;
  previous?: string;
};

export type CreateNewConversationPayload = {
  agent_name: string;
  context_metadata: AgentContext;
  name: string;
};

export type CreateNewConversationResponse = {
  object: 'conversation';
  id: string;
  agent_name: string;
  context_metadata: AgentContext;
  name: string;
  created_at: string;
  updated_at: string;
  messages: Array<never>;
};

export type AttachmentMeta = { uuid: string; fileName: string };

export type PostMessagePayload = {
  input_message: string;
  context: AgentContext;
  attachments?: string[];
};

export const FEEDBACK_POSITIVE = 1;
export const FEEDBACK_NEGATIVE = -1;

export type DebugStep =
  | { type: 'llm'; text: string }
  | { type: 'tool_call'; name: string; args: Record<string, unknown> }
  | { type: 'tool_result'; name: string; output: string };

export type PostMessageResponse = {
  object: 'assistant_message';
  id: string; // UUID in hex format
  thread_id: string; // UUID in hex format
  input_message: string;
  response_message: string;
  status: string;
  response: string;
  feedback?: number; // 1 or -1
  feedback_response?: string | null;
  actions: AgentAction[];
  app_actions: AgentAppAction[];
  choices?: AgentChoiceOption[];
  pending_approval?: PendingApproval;
  cited_sources?: SourceRef[];
  content?: MessageContent[];
  debug_messages?: DebugStep[] | null;
  created_at: string; // ISO 8601 datetime
  updated_at: string; // ISO 8601 datetime
};

/** One labelled fact about a queued action, e.g. `{ label: 'Formula', value: '{Amount} * {Rate}' }`. */
export type ApprovalDetail = {
  label: string;
  value: string;
};

/**
 * Human-readable summary of a queued action, rendered by the backend from the tool's
 * validated arguments. The agent does not author this text, so it cannot misstate what
 * is about to run.
 */
export type ApprovalAction = {
  title: string;
  details: ApprovalDetail[];
};

export type PendingApprovalToolCall = {
  tool_name: string;
};

/**
 * A write action the agent has queued but not executed, awaiting a human decision.
 *
 * `approval_id` is the correlation identity: controls bind to it and submit it back, so a
 * stale card cannot resolve a newer pending action and the same action cannot be executed
 * twice. Never correlate an approval by button label or by "latest message".
 */
export type PendingApproval = {
  approval_id: string;
  expires_at: string; // ISO 8601 datetime
  action: ApprovalAction;
  tool_calls: PendingApprovalToolCall[];
};

export type ApprovalDecision = 'approved' | 'denied';

export type ApprovalDecisionPayload = {
  decision: ApprovalDecision;
};

/**
 * Structured content blocks that an agent can emit for inline rendering.
 * Tools populate these via Pydantic `content: list[dict]` on their output model;
 * the backend serializer surfaces them on the message response.
 *
 * The agent-facing contract is intentionally simple and LLM-friendly. The frontend
 * adapter (`chartBlockAdapter.ts`) translates these into MetricDatum / axis-key
 * arrays / configs for `@captivateiq/visualizations`.
 */
type DataPoint = { label: string; value: number; series?: string };

export type BarChartBlock = {
  type: 'chart';
  chartType: 'bar';
  title?: string;
  description?: string;
  data: DataPoint[];
  xAxisLabel?: string;
  yAxisLabel?: string;
  yAxisFormat?: 'currency' | 'number' | 'percentage';
  /** ISO 4217 currency code (e.g. "USD", "EUR"). Used when yAxisFormat is "currency". Defaults to "USD". */
  currencyCode?: string;
  orientation?: 'horizontal' | 'vertical';
  targetLine?: number;
};

export type LineChartBlock = {
  type: 'chart';
  chartType: 'line';
  title?: string;
  description?: string;
  data: DataPoint[];
  xAxisLabel?: string;
  yAxisLabel?: string;
  yAxisFormat?: 'currency' | 'number' | 'percentage';
  /** ISO 4217 currency code (e.g. "USD", "EUR"). Used when yAxisFormat is "currency". Defaults to "USD". */
  currencyCode?: string;
  targetLine?: number;
  hideMarkers?: boolean;
};

export type PieChartBlock = {
  type: 'chart';
  chartType: 'pie';
  title?: string;
  description?: string;
  data: Array<{ label: string; value: number }>;
  valueFormat?: 'currency' | 'number' | 'percentage';
  /** ISO 4217 currency code (e.g. "USD", "EUR"). Used when valueFormat is "currency". Defaults to "USD". */
  currencyCode?: string;
};

export type ScatterPlotBlock = {
  type: 'chart';
  chartType: 'scatter';
  title?: string;
  description?: string;
  data: Array<{ x: number; y: number; label?: string; series?: string }>;
  xAxisLabel?: string;
  yAxisLabel?: string;
  xAxisFormat?: 'currency' | 'number' | 'percentage';
  yAxisFormat?: 'currency' | 'number' | 'percentage';
  /** ISO 4217 currency code (e.g. "USD", "EUR"). Used when an axis format is "currency". Defaults to "USD". */
  currencyCode?: string;
};

export type HistogramBlock = {
  type: 'chart';
  chartType: 'histogram';
  title?: string;
  description?: string;
  data: Array<{ value: number }>;
  binCount?: number;
  xAxisLabel?: string;
  yAxisLabel?: string;
  xAxisFormat?: 'currency' | 'number';
  /** ISO 4217 currency code (e.g. "USD", "EUR"). Used when xAxisFormat is "currency". Defaults to "USD". */
  currencyCode?: string;
};

export type SummaryCardBlock = {
  type: 'summary_card';
  title: string;
  value: number;
  format?: 'currency' | 'number' | 'percentage';
  /** ISO 4217 currency code (e.g. "USD", "EUR"). Used when format is "currency". Defaults to "USD". */
  currencyCode?: string;
  comparison?: { label: string; value: number; compareAs: 'number' | 'percentage' };
  sparkline?: Array<{ x: string | number; y: number }>;
};

export type ChartBlock = BarChartBlock | LineChartBlock | PieChartBlock | ScatterPlotBlock | HistogramBlock;

export type TableBlock = {
  type: 'table';
  title?: string;
  headers: string[];
  rows: Array<Array<string | number>>;
  caption?: string;
};

export type ColumnTraceNode = {
  type?: 'column' | 'transformation' | 'unknown';
  id: string;
  name: string;
  worksheetName: string;
  variant: string;
  dataType: string;
  formula?: string | null;
  value?: string | null;
  isRoot?: boolean;
  tags?: string[];
  links?: Record<string, string>;
};

export type ColumnTraceEdge = {
  source: string;
  target: string;
  isCrossSchema?: boolean;
};

export type ColumnTraceBlock = {
  type: 'column_trace';
  title: string;
  rootColumnId: string;
  nodes: ColumnTraceNode[];
  edges: ColumnTraceEdge[];
};

export type MessageContent = ChartBlock | TableBlock | SummaryCardBlock | ColumnTraceBlock;

export type GetConversationsQueryParams = {
  offset?: number;
  limit?: number;
};

export type GetConversationsResponse = APIList<ConversationListItem>;

export type ConversationListItem = {
  object: 'conversation';
  id: string;
  agent_name: string;
  name: string;
  created_at: string; // ISO 8601 datetime
  updated_at: string; // ISO 8601 datetime
};

export type GetSingleConversationResponse = {
  object: 'conversation';
  id: string;
  agent_name: string;
  context_metadata: Record<string, string | number>;
  name: string;
  created_at: string;
  updated_at: string;
  messages: Array<AgentMessage>;
};

export type AgentMessage = {
  object: 'assistant_message';
  id: string; // UUID in hex format
  thread_id?: string; // UUID in hex format
  input_message: string;
  response_message: string;
  status: string;
  response: string;
  feedback?: number; // 1 or -1
  feedback_response?: string | null;
  actions: AgentAction[];
  app_actions: AgentAppAction[];
  choices?: AgentChoiceOption[];
  pending_approval?: PendingApproval;
  cited_sources?: SourceRef[];
  content?: MessageContent[];
  debug_messages?: DebugStep[] | null;
  created_at: string; // ISO 8601 datetime
  updated_at: string; // ISO 8601 datetime
};

// Alias for backwards compatibility
export type AssistantMessage = AgentMessage;

type AgentAction = {
  object: 'assistant_action';
  id: string; // UUID in hex format
  message_id: string; // UUID
  tool_name: string;
  status: string;
  order: number;
  response: string;
  actioned_at: string; // ISO 8601 datetime
  created_at: string; // ISO 8601 datetime
  updated_at: string; // ISO 8601 datetime
};

export type SourceRef = {
  url: string;
  label: string;
  tool_name?: string;
  data_context?: string;
};

export type AgentAppAction = {
  name: string;
  action_data: Record<string, any>;
};

export type AgentChoiceOption = {
  label: string;
  description: string;
};

export type AgentContext = Record<string, string | number | undefined>;

/**
 * Agent name identifiers matching backend kit_name values.
 * These are used for consistent agent identification across frontend and backend.
 */
export enum AgentNames {
  PLAN_DOCUMENT_AGENT = 'planexecution.assistant.plan_document_agent',
  PAYOUTS_INFO_AGENT = 'payouts.assistant.payouts_info_agent',
  /* oxlint-disable typescript/no-duplicate-enum-values --
     PLAN_COMPREHENSION_AGENT, FORMULAS_AGENT, MODELING_AGENT below are intentional
     deprecated aliases of COMP_BUILDER_AGENT */
  COMP_BUILDER_AGENT = 'worksheets.comp_builder_agent',
  /** @deprecated Use COMP_BUILDER_AGENT instead */
  MODELING_AGENT = 'worksheets.comp_builder_agent',
  WHAT_IF_AGENT = 'payee.assistant.what_if_agent',
  /** @deprecated Use COMP_BUILDER_AGENT instead */
  PLAN_COMPREHENSION_AGENT = 'worksheets.comp_builder_agent',
  /** @deprecated Use COMP_BUILDER_AGENT instead */
  FORMULAS_AGENT = 'worksheets.comp_builder_agent',
  /* oxlint-enable typescript/no-duplicate-enum-values */
  CATALYST_ML_TRAINING_AGENT = 'catalyst.ml.training_run_explainer',
  CATALYST_ML_PREDICTION_AGENT = 'catalyst.ml.prediction_explainer',
  INQUIRIES_AGENT = 'disputes.inquiries',
  PAYEE_AGENT = 'payee.assistant.payee_agent',
  TRANSFORMATION_AGENT = 'worksheets.kits.transformation',
  ACCOUNT_DEALER_AGENT = 'tq.account_dealer',
  REVOPS_AGENT = 'tq.revops_agent',
  CATALYST_EPM_AGENT = 'catalyst.epm.agent',
  EPM_FINANCIAL_MODEL_BUILDER_AGENT = 'epm.financial_model.builder',
  AUDIT_LOGS_AGENT = 'audit.assistkit.audit_logs_agent',
}

export type Message = {
  id: string;
  type: 'user' | 'agent';
  contents: string;
  cited_sources?: SourceRef[];
  content?: MessageContent[];
  attachments?: AttachmentMeta[];
  debug_messages?: DebugStep[];
};

export type MessageButtonSpec = {
  id: string;
  icon: IconDefinition;
  onClick: () => void;
  tooltip?: string;
  disabled?: boolean;
  /** When provided, renders a labeled button instead of an icon-only button. */
  label?: string;
  /** Button variant when rendering as a labeled button. Defaults to 'outlined'. */
  variant?: 'contained' | 'outlined' | 'text';
};

export type AppActionButtonPosition = 'top' | 'bottom' | 'left' | 'right';

export type AppActionConfig = {
  name: string;
  buttons?: MessageButtonSpec[];
  buttonPosition?: AppActionButtonPosition;
};

export type AppActionContext = {
  userMessageId: string;
  agentMessageId: string;
};

export type AsyncMessageSubmitResponse = { status: 'pending'; request_id: string };
export type AsyncMessageStatusResponse =
  | { status: 'pending' }
  | { status: 'completed'; data: PostMessageResponse }
  /**
   * `status_code` is the HTTP status the equivalent synchronous endpoint would have returned. A
   * polled failure has no response to read one off, so the task reports it here — without it, a
   * conflict is indistinguishable from a real error and cannot be resynced. Optional because
   * older workers predate the field.
   */
  | { status: 'error'; error: string; error_type?: string; status_code?: number };
